import { Link } from "react-router-dom";
import ctaBuyImg from "@/assets/cta-buy.jpg";
import ctaSellImg from "@/assets/cta-sell.jpg";
import ctaInvestImg from "@/assets/cta-invest.jpg";
import serviceBuyImg from "@/assets/service-buy.jpg";
import servicePrepImg from "@/assets/service-prep.jpg";
import serviceInvestImg from "@/assets/service-invest.jpg";
// CTA images loaded
import { Home, TrendingUp, Paintbrush, Shield, Users, Globe, Phone, MapPin, DollarSign, Calendar, Star, Building, Key, Search, Heart, BarChart3, Menu, Compass } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import ContentSection from "@/components/sections/ContentSection";
import CardGrid from "@/components/sections/CardGrid";
import SectionHeader from "@/components/sections/SectionHeader";
import CTABlock from "@/components/sections/CTABlock";
import SplitSection from "@/components/sections/SplitSection";
import ProcessSteps from "@/components/sections/ProcessSteps";
import FeatureGrid from "@/components/sections/FeatureGrid";
import PageHero from "@/components/sections/PageHero";
import FeaturedPropertyCard from "@/components/sections/FeaturedPropertyCard";
import PropertyGrid from "@/components/sections/PropertyGrid";
import HorizontalScrollSection from "@/components/sections/HorizontalScrollSection";
import MediaHero from "@/components/sections/MediaHero";
import StatsSplit from "@/components/sections/StatsSplit";
import OverlayContentCard from "@/components/sections/OverlayContentCard";
import ArticlePreviewCard from "@/components/sections/ArticlePreviewCard";
import ImageFeatureCard from "@/components/sections/ImageFeatureCard";
import EditorialFeatureCard from "@/components/sections/EditorialFeatureCard";
import AccordionGroup from "@/components/sections/AccordionGroup";
import TabSection from "@/components/sections/TabSection";
import TrustBar from "@/components/sections/TrustBar";
import StatsSection from "@/components/sections/StatsSection";
import TestimonialCard from "@/components/sections/TestimonialCard";
import Checklist from "@/components/sections/Checklist";
import Breadcrumbs from "@/components/sections/Breadcrumbs";
import StickyNav from "@/components/sections/StickyNav";
import StatusBadge from "@/components/sections/StatusBadge";
import ComparisonBlock from "@/components/sections/ComparisonBlock";
import Timeline from "@/components/sections/Timeline";
import LeadCapture from "@/components/sections/LeadCapture";
import PropertyDetailLayout from "@/components/sections/PropertyDetailLayout";
import InfoCard from "@/components/sections/InfoCard";
import MultiCTA from "@/components/sections/MultiCTA";
import { IconShowcase } from "@/components/sections/IconSystem";
import DefaultHero from "@/components/sections/DefaultHero";
import SearchHero from "@/components/sections/SearchHero";
import SearchFilterThin from "@/components/sections/SearchFilterThin";
import MinimalHero from "@/components/sections/MinimalHero";
import RevealSection from "@/components/sections/RevealSection";
import HoverPremiumCard from "@/components/sections/HoverPremiumCard";
import StackingCards from "@/components/sections/StackingCards";
import StickyStory from "@/components/sections/StickyStory";
import ImageCallout from "@/components/sections/ImageCallout";
import AnimatedTabs from "@/components/sections/AnimatedTabs";
import EditorialServiceBlock from "@/components/sections/EditorialServiceBlock";
import TestimonialsSection from "@/components/TestimonialsSection";
import BuyerStrategySection, { buyerStrategySectionMeta } from "@/components/sections/BuyerStrategySection";
import { Button } from "@/components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

/* ─── Helpers ─── */
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-8 border-b border-border pb-4">
    {children}
  </h2>
);

const PatternLabel = ({ id, name, description }: { id?: string; name: string; description: string }) => (
  <div id={id} className="mb-8 scroll-mt-28">
    {id && (
      <span className="font-body text-[10px] uppercase tracking-[0.25em] text-muted-foreground/60 mb-1 block">{id}</span>
    )}
    <p className="font-body text-xs uppercase tracking-widest text-accent mb-2">{name}</p>
    <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
  </div>
);

const Swatch = ({ label, bg, text }: { label: string; bg: string; text: string }) => (
  <div className={`${bg} ${text} p-6 flex flex-col justify-end min-h-[100px]`}>
    <span className="font-body text-xs uppercase tracking-widest">{label}</span>
  </div>
);

const styleGuideToc = [
  {
    heading: "Foundations",
    links: [
      { label: "Typography", href: "#FOUNDATION-TYPE-01" },
      { label: "Buttons & Links", href: "#FOUNDATION-BUTTON-01" },
      { label: "Spacing & Layout", href: "#FOUNDATION-SPACING-01" },
      { label: "Color Usage", href: "#FOUNDATION-COLOR-01" },
    ],
  },
  {
    heading: "Core Layout",
    links: [
      { label: "Sections", href: "#LAYOUT-SECTION-01a" },
      { label: "Headers", href: "#LAYOUT-HEADER-01" },
      { label: "Card & Feature Grids", href: "#GRID-FEATURE-01" },
      { label: "Split Sections", href: "#LAYOUT-SPLIT-01" },
    ],
  },
  {
    heading: "Reusable UI",
    links: [
      { label: "Breadcrumbs", href: "#UI-BREADCRUMB-01" },
      { label: "Accordions & Tabs", href: "#UI-ACCORDION-01" },
      { label: "Stats & Trust", href: "#UI-STATS-01" },
      { label: "Testimonials", href: "#SECTION-TESTIMONIALS-01" },
      { label: "Buyer Strategy", href: "#SECTION-BUYER-STRATEGY-01" },
    ],
  },
  {
    heading: "Premium Patterns",
    links: [
      { label: "Premium Card System", href: "#CARD-ARTICLE-01" },
      { label: "Article Preview Card", href: "#CARD-ARTICLE-01" },
      { label: "Image Feature Card", href: "#CARD-IMAGE-01" },
      { label: "Editorial Feature Card", href: "#CARD-EDITORIAL-01" },
      { label: "Media Hero", href: "#HERO-MEDIA-01" },
      { label: "Property Cards", href: "#CARD-PROPERTY-01" },
      { label: "Overlay Cards", href: "#CARD-OVERLAY-01" },
    ],
  },
  {
    heading: "Hero Variants",
    links: [
      { label: "Default Hero", href: "#HERO-DEFAULT-01a" },
      { label: "Search Hero", href: "#HERO-SEARCH-01" },
      { label: "Search Filter Thin", href: "#SEARCH-FILTER-THIN-01" },
      { label: "Minimal Hero", href: "#HERO-MINIMAL-01a" },
    ],
  },
  {
    heading: "Advanced UX",
    links: [
      { label: "Sticky Nav", href: "#UX-STICKY-NAV-01" },
      { label: "Comparison Block", href: "#UX-COMPARISON-01" },
      { label: "Timeline", href: "#UX-TIMELINE-01" },
      { label: "Lead Capture", href: "#UX-LEAD-01" },
    ],
  },
  {
    heading: "Accessibility",
    links: [
      { label: "Focus States", href: "#A11Y-FOCUS-01" },
      { label: "Contrast", href: "#A11Y-CONTRAST-01" },
      { label: "Semantic Structure", href: "#A11Y-SEMANTIC-01" },
      { label: "Reduced Motion", href: "#A11Y-MOTION-01" },
    ],
  },
  {
    heading: "Motion",
    links: [
      { label: "Motion Standards", href: "#MOTION-SYSTEM-01" },
      { label: "Reveal Section", href: "#MOTION-REVEAL-01" },
      { label: "Hover Cards", href: "#INTERACT-HOVER-CARD-01" },
      { label: "Sticky Story", href: "#INTERACT-STICKY-STORY-01" },
      { label: "Image Callout", href: "#INTERACT-IMAGE-CALLOUT-01" },
    ],
  },
];

/* ─── Page ─── */
const StyleGuide = () => (
  <SiteLayout>
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="gold"
          className="fixed right-5 bottom-5 z-50 shadow-lg px-5 py-4 h-auto gap-2"
          aria-label="Open style guide table of contents"
        >
          <Menu className="w-4 h-4" />
          Navigate
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[90vw] sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Style Guide Navigation</SheetTitle>
        </SheetHeader>
        <div className="mt-8 space-y-8">
          {styleGuideToc.map((group) => (
            <div key={group.heading}>
              <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-3">
                {group.heading}
              </p>
              <div className="space-y-1">
                {group.links.map((link) => (
                  <SheetClose asChild key={link.href}>
                    <a
                      href={link.href}
                      className="block border border-transparent px-3 py-2 text-sm text-foreground hover:bg-secondary/5 hover:border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2"
                    >
                      {link.label}
                    </a>
                  </SheetClose>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>

    {/* Page hero */}
    <section className="bg-secondary pt-40 pb-24 md:pt-48 md:pb-32">
      <div className="mx-auto max-w-content px-5 md:px-12 text-center">
        <h1 className="font-headline text-4xl sm:text-5xl md:text-6xl lg:text-[5.25rem] text-secondary-foreground leading-[1.08]">
          Component Library
        </h1>
        <p className="mt-5 md:mt-7 text-secondary-foreground/70 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
          Complete visual reference for every reusable element and section pattern in the Hadassah Homes design system.
        </p>
      </div>
    </section>

    {/* ════════════════════════════════════════════════════════════
        SECTION 1 — FOUNDATIONS
       ════════════════════════════════════════════════════════════ */}
    <ContentSection>
      <SectionLabel>1 · Foundations</SectionLabel>
    </ContentSection>

    {/* 1a. Typography */}
    <ContentSection>
      <PatternLabel id="FOUNDATION-TYPE-01" name="Typography" description="EB Garamond for headlines, Manrope for body and labels. Base: 18px mobile → 20px desktop. Responsive scale." />
      <div className="space-y-8">
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-2">H1 — font-headline · 4xl → 5.25rem</p>
          <h1 className="font-headline text-4xl sm:text-5xl md:text-6xl lg:text-[5.25rem] text-foreground leading-[1.08]">Luxury Real Estate, Rooted in Trust</h1>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-2">H2 — font-headline · 4xl → 6xl</p>
          <h2 className="font-headline text-4xl sm:text-[2.75rem] md:text-5xl lg:text-6xl text-foreground leading-tight">Our Commitment to Excellence</h2>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-2">H3 — font-headline · xl → 2xl</p>
          <h3 className="font-headline text-xl md:text-2xl text-foreground">Expert Guidance at Every Step</h3>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-2">Body — font-body · base → lg (20px desktop)</p>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl">
            Whether you're a first-time buyer exploring Massachusetts or an investor eyeing the Dominican Republic, our team brings decades of combined experience to guide you confidently through every decision.
          </p>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-2">Label — font-body · sm · uppercase · tracking-widest</p>
          <span className="font-body text-sm uppercase tracking-widest text-accent">Featured Property</span>
        </div>
      </div>
    </ContentSection>

    {/* 1b. Buttons & Links */}
    <ContentSection variant="dark">
      <PatternLabel id="FOUNDATION-BUTTON-01" name="Buttons & Links" description="Primary, secondary, and text link styles used across the site." />
      <div className="space-y-8">
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-secondary-foreground/60 mb-4">Primary CTA</p>
          <button className="bg-accent text-accent-foreground font-body text-sm uppercase tracking-widest px-8 py-3 hover:bg-accent/90 transition-colors">Book a Consultation</button>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-secondary-foreground/60 mb-4">Secondary / Outline</p>
          <button className="border border-accent text-accent font-body text-sm uppercase tracking-widest px-8 py-3 hover:bg-accent hover:text-accent-foreground transition-colors">View Listings</button>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-secondary-foreground/60 mb-4">Text Link</p>
          <Link to="/consultation" className="font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors">Learn More →</Link>
        </div>
      </div>
    </ContentSection>

    {/* 1c. Spacing & Layout */}
    <ContentSection>
      <PatternLabel id="FOUNDATION-SPACING-01" name="Spacing & Layout" description="Container widths, vertical rhythms, and horizontal padding standards." />
      <div className="space-y-10">
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-4">Container widths</p>
          <div className="space-y-3">
            <div className="bg-accent/10 border border-accent/20 p-4">
              <span className="font-body text-xs uppercase tracking-widest text-accent">max-w-content · 800px</span>
              <p className="text-muted-foreground text-sm mt-1">Text-heavy sections, articles, focused content.</p>
            </div>
            <div className="bg-accent/10 border border-accent/20 p-4 max-w-wide mx-auto">
              <span className="font-body text-xs uppercase tracking-widest text-accent">max-w-wide · 1200px</span>
              <p className="text-muted-foreground text-sm mt-1">Card grids, navigation containers, wider layouts.</p>
            </div>
          </div>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-4">Section vertical padding</p>
          <div className="space-y-3">
            <div className="bg-secondary/10 border border-border p-4"><span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Standard: py-16 md:py-24</span></div>
            <div className="bg-secondary/10 border border-border p-4"><span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Hero compact: pt-32 pb-16 md:pt-40 md:pb-20</span></div>
            <div className="bg-secondary/10 border border-border p-4"><span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Hero full: pt-36 pb-20 md:pt-44 md:pb-28</span></div>
          </div>
        </div>
      </div>
    </ContentSection>

    {/* 1d. Color Usage */}
    <ContentSection variant="dark">
      <PatternLabel id="FOUNDATION-COLOR-01" name="Color Usage" description="Background, text, and accent color tokens across light and dark contexts." />
      <div className="space-y-8">
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-secondary-foreground/60 mb-4">Background variations</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <Swatch label="Background (Cream)" bg="bg-background" text="text-foreground" />
            <Swatch label="Secondary (Deep Green)" bg="bg-secondary" text="text-secondary-foreground" />
            <Swatch label="Primary (Rich Black)" bg="bg-primary" text="text-primary-foreground" />
            <Swatch label="Card (White)" bg="bg-card" text="text-card-foreground" />
          </div>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-secondary-foreground/60 mb-4">Text color hierarchy</p>
          <div className="space-y-3 bg-background p-6">
            <p className="text-foreground font-body text-sm"><strong>foreground</strong> — Primary text, headings</p>
            <p className="text-muted-foreground font-body text-sm"><strong className="text-foreground">muted-foreground</strong> — Body text, descriptions</p>
            <p className="text-accent font-body text-sm"><strong>accent</strong> — Links, labels, interactive elements</p>
            <p className="text-muted-foreground/50 font-body text-sm"><strong className="text-foreground">muted-foreground/50</strong> — Captions, fine print</p>
          </div>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-secondary-foreground/60 mb-4">Accent usage</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-accent p-6"><span className="font-body text-xs uppercase tracking-widest text-accent-foreground">Accent (Gold) — CTAs</span></div>
            <div className="bg-background border border-accent p-6"><span className="font-body text-xs uppercase tracking-widest text-accent">Accent border — Outlines</span></div>
            <div className="bg-accent/10 p-6"><span className="font-body text-xs uppercase tracking-widest text-accent">Accent/10 — Highlights</span></div>
          </div>
        </div>
      </div>
    </ContentSection>

    {/* ════════════════════════════════════════════════════════════
        SECTION 2 — CORE LAYOUT / SECTION PATTERNS
       ════════════════════════════════════════════════════════════ */}
    <ContentSection>
      <SectionLabel>2 · Core Layout Patterns</SectionLabel>
      <p className="text-muted-foreground text-sm leading-relaxed">Reusable layout blocks used across all pages.</p>
    </ContentSection>

    {/* 2a. PageHero */}
    <PageHero
      title="PageHero Component"
      subtitle="A full-width hero with headline, subtitle, and optional background image. Used as the primary entry point on every page."
    />

    {/* 2b. ContentSection variants */}
    <ContentSection>
      <PatternLabel id="LAYOUT-SECTION-01a" name="ContentSection — Light Variant" description="Default cream background. Used for text-heavy sections and general content." />
      <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">This is a live example of ContentSection with the default light variant. Content is constrained to max-w-content (800px) for comfortable readability.</p>
    </ContentSection>

    <ContentSection variant="dark">
      <PatternLabel id="LAYOUT-SECTION-01b" name="ContentSection — Dark Variant" description="Deep green background. Used for contrast sections and to break visual rhythm." />
      <p className="text-secondary-foreground/60 text-sm leading-relaxed max-w-2xl">This is the dark variant of ContentSection, using the secondary color palette.</p>
    </ContentSection>

    <ContentSection variant="accent">
      <PatternLabel id="LAYOUT-SECTION-01c" name="ContentSection — Accent Variant" description="Rich black background. Used for high-emphasis sections and premium feel." />
      <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">This is the accent variant, using the primary/rich black background for maximum visual weight.</p>
    </ContentSection>

    {/* 2c. SectionHeader */}
    <ContentSection>
      <PatternLabel id="LAYOUT-HEADER-01" name="SectionHeader" description="Reusable header with optional eyebrow, title, and description. Supports left and center alignment." />
      <div className="space-y-12">
        <SectionHeader eyebrow="Center Aligned" title="Our Services" description="A versatile section header with optional eyebrow, title, and description." />
        <SectionHeader align="left" eyebrow="Left Aligned" title="Why Choose Hadassah Homes" description="Left-aligned variant for asymmetric layouts and split sections." />
      </div>
    </ContentSection>

    {/* 2d. CardGrid */}
    <ContentSection variant="dark">
      <PatternLabel id="GRID-BASE-01" name="CardGrid" description="Responsive grid of cards with icon, title, and description. Supports 2–4 columns." />
      <div className="mx-auto max-w-wide">
        <CardGrid
          columns={3}
          items={[
            { icon: Home, title: "Buy a Home", description: "Expert guidance through every step of the home buying process." },
            { icon: TrendingUp, title: "Invest in Real Estate", description: "Build long-term wealth through strategic investments." },
            { icon: Paintbrush, title: "Interior Design", description: "Transform your space with professional design." },
          ]}
        />
      </div>
    </ContentSection>

    {/* 2e. FeatureGrid */}
    <ContentSection>
      <PatternLabel id="GRID-FEATURE-01" name="FeatureGrid" description="Semantic wrapper around CardGrid specifically for features/benefits. Supports icons and flexible column counts." />
      <div className="mx-auto max-w-wide">
        <FeatureGrid
          columns={3}
          items={[
            { icon: Shield, title: "Licensed & Insured", description: "Full licensing across Massachusetts and the Dominican Republic." },
            { icon: Users, title: "Dedicated Team", description: "A personal advisor assigned to you from day one." },
            { icon: Globe, title: "Two Markets", description: "Expertise in both domestic and Caribbean real estate." },
          ]}
        />
      </div>
    </ContentSection>

    {/* 2f. EditorialServiceBlock */}
    <ContentSection variant="dark">
      <PatternLabel id="EDITORIAL-SERVICE-01" name="EditorialServiceBlock" description="Editorial-style service section with emoji, title, intro, description, checklist, nudge, and CTA. Used on hub pages like Work With Us." />
      <EditorialServiceBlock
        title="Buy a Home"
        intro="Buy with clarity, confidence, and a competitive edge."
        description={["We guide you through every step of the process, from understanding the market and securing financing to identifying the right property and making a strong offer."]}
        checklists={[{ label: "What you can expect:", items: ["A clear, step-by-step buying strategy", "Insight into Massachusetts markets and opportunities", "Support tailored for both first-time and experienced buyers"] }]}
        nudge="Start your home search the right way"
        cta={{ text: "Learn More About Buying a Home", href: "/buy-a-home" }}
      />
    </ContentSection>

    {/* 2g. CTABlock */}
    <ContentSection>
      <PatternLabel id="CTA-PRIMARY-01" name="CTABlock" description="Full-width call-to-action section with primary and optional secondary CTA." />
    </ContentSection>
    <CTABlock
      title="Ready to Start Your Real Estate Journey?"
      description="Whether you're buying your first home or expanding an investment portfolio, our team is here to help."
      primaryCta={{ text: "Book a Consultation", href: "/consultation" }}
      secondaryCta={{ text: "View Listings", href: "/listings" }}
    />

    {/* 2g. ProcessSteps — horizontal */}
    <ContentSection>
      <PatternLabel id="UX-PROCESS-01a" name="ProcessSteps — Horizontal" description="Step-by-step flow in a horizontal row. Ideal for 3–4 step processes." />
      <ProcessSteps
        layout="horizontal"
        steps={[
          { title: "Initial Consultation", description: "We learn about your goals, timeline, and budget." },
          { title: "Market Research", description: "Our team identifies opportunities and presents curated options." },
          { title: "Offer & Negotiation", description: "We handle the details to secure the best terms." },
          { title: "Close & Celebrate", description: "Seamless closing coordination so you can focus on what matters." },
        ]}
      />
    </ContentSection>

    {/* 2h. ProcessSteps — vertical */}
    <ContentSection variant="dark">
      <PatternLabel id="UX-PROCESS-01b" name="ProcessSteps — Vertical" description="Step-by-step flow in a vertical timeline. Better for longer processes or mobile-first layouts." />
      <ProcessSteps
        layout="vertical"
        steps={[
          { title: "Discovery Call", description: "Share your vision, budget, and timeline with our team." },
          { title: "Property Search", description: "We curate a shortlist of properties matching your criteria." },
          { title: "Site Visits", description: "Tour your top picks with an experienced advisor." },
          { title: "Due Diligence", description: "We coordinate inspections, appraisals, and legal review." },
          { title: "Closing Day", description: "Sign, celebrate, and receive the keys to your new property." },
        ]}
      />
    </ContentSection>

    {/* 2i. SplitSection */}
    <ContentSection>
      <PatternLabel id="LAYOUT-SPLIT-01" name="SplitSection" description="Left/right layout for text + visual. Supports reverse layout and optional CTA." />
      <SplitSection
        title="A Trusted Partner in Every Market"
        description="With deep roots in Massachusetts and expanding presence in the Dominican Republic, Hadassah Homes brings local expertise and global perspective to every transaction."
        image="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80"
        imageAlt="Luxury home exterior"
        cta={{ text: "About Our Team", href: "/about" }}
      />
    </ContentSection>

    <ContentSection variant="dark">
      <PatternLabel id="LAYOUT-SPLIT-01b" name="SplitSection — Reversed" description="Reversed layout with the visual on the left." />
      <SplitSection
        title="Investment Opportunities in the Caribbean"
        description="Discover high-growth real estate opportunities in the Dominican Republic, from beachfront villas to urban developments."
        image="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80"
        imageAlt="Caribbean villa"
        cta={{ text: "Explore Dominican Republic", href: "/dominican-republic" }}
        reverse
      />
    </ContentSection>

    {/* ════════════════════════════════════════════════════════════
        SECTION 3 — REUSABLE UI ELEMENTS
       ════════════════════════════════════════════════════════════ */}
    <ContentSection>
      <SectionLabel>3 · Reusable UI Elements</SectionLabel>
      <p className="text-muted-foreground text-sm leading-relaxed">Smaller, composable components used inside sections across multiple pages.</p>
    </ContentSection>

    {/* 3a. Breadcrumbs */}
    <ContentSection>
      <PatternLabel id="UI-BREADCRUMB-01" name="Breadcrumbs" description="SEO-friendly navigation with JSON-LD structured data. Used on interior pages for wayfinding." />
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Services", href: "/work-with-us" }, { label: "Buy a Home" }]} />
    </ContentSection>

    {/* 3b. AccordionGroup */}
    <ContentSection variant="dark">
      <PatternLabel id="UI-ACCORDION-01" name="AccordionGroup" description="Expandable sections for FAQ, details, and collapsible content. Supports single or multiple open items." />
      <AccordionGroup
        items={[
          { title: "What areas do you serve?", content: "We serve all of Massachusetts with a focus on Greater Boston, the South Shore, and Cape Cod. Internationally, we operate across key markets in the Dominican Republic including Punta Cana, Santo Domingo, and Las Terrenas." },
          { title: "Do you work with first-time buyers?", content: "Absolutely. A significant portion of our clients are first-time buyers. We provide hands-on guidance through every step, from pre-approval to closing day." },
          { title: "What makes Hadassah Homes different?", content: "We combine deep local market expertise with a concierge-level service model. Every client receives a dedicated advisor, transparent communication, and access to our full network of trusted professionals." },
          { title: "Do you offer investment consulting?", content: "Yes. We provide comprehensive investment analysis, portfolio strategy, and property management referrals for both domestic and international investors." },
        ]}
      />
    </ContentSection>

    {/* 3c. TabSection */}
    <ContentSection>
      <PatternLabel id="UI-TABS-01" name="TabSection" description="Tab switcher for comparing content categories. Clean switching behavior with state management." />
      <TabSection
        tabs={[
          {
            label: "Buyers",
            content: (
              <div className="space-y-3">
                <h3 className="font-headline text-xl text-foreground">For Buyers</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">From pre-approval to closing, our buyer advisory service provides white-glove support. We negotiate on your behalf and coordinate every detail so you can focus on finding the perfect home.</p>
              </div>
            ),
          },
          {
            label: "Sellers",
            content: (
              <div className="space-y-3">
                <h3 className="font-headline text-xl text-foreground">For Sellers</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">Strategic pricing, professional staging, and targeted marketing ensure your property reaches the right buyers. Our sellers consistently achieve above-market results.</p>
              </div>
            ),
          },
          {
            label: "Investors",
            content: (
              <div className="space-y-3">
                <h3 className="font-headline text-xl text-foreground">For Investors</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">Data-driven analysis and emerging market access help you build a diversified real estate portfolio. We identify high-yield opportunities in both Massachusetts and the Dominican Republic.</p>
              </div>
            ),
          },
        ]}
      />
    </ContentSection>

    {/* 3d. TrustBar */}
    <ContentSection variant="dark">
      <PatternLabel id="UI-TRUST-01a" name="TrustBar — Dark Variant" description="Horizontal credibility strip for stats and social proof. Supports light and dark variants." />
    </ContentSection>
    <TrustBar
      variant="dark"
      items={[
        { value: "$120M+", label: "Transaction Volume" },
        { value: "350+", label: "Families Served" },
        { value: "98%", label: "Client Satisfaction" },
        { value: "15+", label: "Years Experience" },
      ]}
    />
    <ContentSection>
      <PatternLabel id="UI-TRUST-01b" name="TrustBar — Light Variant" description="Light background version for use on cream sections." />
    </ContentSection>
    <TrustBar
      variant="light"
      items={[
        { value: "2", label: "Countries" },
        { value: "5★", label: "Google Rating" },
        { value: "24/7", label: "Client Support" },
        { value: "100%", label: "Referral Rate" },
      ]}
    />

    {/* 3e. StatsSection */}
    <ContentSection>
      <PatternLabel id="UI-STATS-01" name="StatsSection" description="Simple grid of key numbers. Clean, minimal layout for impact metrics." />
      <StatsSection
        columns={4}
        stats={[
          { value: "$120M+", label: "In Transactions" },
          { value: "350+", label: "Happy Clients" },
          { value: "98%", label: "Satisfaction Rate" },
          { value: "15+", label: "Years of Experience" },
        ]}
      />
    </ContentSection>

    {/* 3f. TestimonialCard */}
    <ContentSection variant="dark">
      <PatternLabel id="UI-TESTIMONIAL-01" name="TestimonialCard" description="Individual testimonial card with quote, name, role. Works inside grids or sliders." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <TestimonialCard
          quote="Hadassah Homes made our first home purchase feel effortless. Their attention to detail and market knowledge gave us complete confidence."
          name="Maria & David Chen"
          role="First-time Homebuyers, Boston"
        />
        <TestimonialCard
          quote="The investment analysis they provided helped us identify a property that has appreciated 22% in just two years. Exceptional team."
          name="James Richardson"
          role="Real Estate Investor"
          showStars
        />
        <TestimonialCard
          quote="From staging to final negotiation, they handled everything with professionalism. We sold above asking price in under two weeks."
          name="Sophia Laurent"
          role="Home Seller, Cambridge"
        />
      </div>
    </ContentSection>

    {/* 3f.1 TestimonialsSection */}
    <ContentSection>
      <PatternLabel
        id="SECTION-TESTIMONIALS-01"
        name="TestimonialsSection"
        description="Homepage testimonial carousel section with large heading, sliding cards, arrows, and pagination dots. Reusable across service pages."
      />
    </ContentSection>
    <TestimonialsSection
      sectionId="SECTION-TESTIMONIALS-01"
      title="What Our Buyers Say"
      testimonials={[
        {
          name: "First-Time Buyer",
          text: "Hadassah Homes made our first purchase feel thoughtful and manageable. We always knew what the next step was and never felt pressured.",
        },
        {
          name: "Relocating Buyer",
          text: "We were moving on a tight timeline and needed strong local guidance. The team helped us evaluate options quickly without sacrificing long-term fit.",
        },
        {
          name: "Investment-Minded Buyer",
          text: "What stood out was the strategic perspective. They helped us think beyond the listing photos and focus on value, location, and future upside.",
        },
      ]}
    />

    {/* 3g. Checklist */}
    <ContentSection>
      <PatternLabel id="UI-CHECKLIST-01" name="Checklist" description="Icon-based bullet list for benefits and inclusions. Supports 1 or 2 column layouts." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-4">Single column</p>
          <Checklist
            columns={1}
            items={[
              "Dedicated buyer's agent from day one",
              "Pre-approval coordination with preferred lenders",
              "Curated property shortlist based on your criteria",
              "Full negotiation and closing support",
            ]}
          />
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-4">Two columns</p>
          <Checklist
            columns={2}
            items={[
              "Market analysis",
              "Property valuation",
              "Legal coordination",
              "Inspection scheduling",
              "Title search",
              "Closing support",
            ]}
          />
        </div>
      </div>
    </ContentSection>

    {/* ════════════════════════════════════════════════════════════
        SECTION 4 — PREMIUM SECTION PATTERNS
       ════════════════════════════════════════════════════════════ */}
    <ContentSection>
      <SectionLabel>4 · Premium Section Patterns</SectionLabel>
      <p className="text-muted-foreground text-sm leading-relaxed">Advanced, composable section components for high-end real estate layouts.</p>
    </ContentSection>

    <ContentSection>
      <PatternLabel
        id="CARD-ARTICLE-01"
        name="ArticlePreviewCard"
        description="Reusable editorial content card for blog posts, guides, and educational resources. Supports both standard and featured layouts."
      />
      <SectionHeader
        title="Premium Card System"
        description="Phase 1 reusable premium cards designed to expand the site's visual vocabulary without changing approved content."
      />
      <div className="grid grid-cols-1 xl:grid-cols-[1.08fr_0.92fr] gap-8 md:gap-10">
        <ArticlePreviewCard
          title="What to Know Before Buying in the Dominican Republic"
          excerpt="A stronger editorial content card for featured guides, resource hubs, and premium educational storytelling with cleaner hierarchy and better visual balance."
          category="Dominican Republic Investing"
          meta="Featured Guide"
          image="https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1200&q=80"
          imageAlt="Coastal real estate development in a resort market"
          href="/blog"
          ctaLabel="Explore Guide"
          featured
        />
        <div className="grid grid-cols-1 gap-6">
          <ArticlePreviewCard
            title="First-Time Buyer Checklist"
            excerpt="A reusable article and guide card for buyer education, resource pages, and support content."
            category="Buying a Home"
            meta="Guide"
            image="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=900&q=80"
            imageAlt="Front exterior of a thoughtfully presented home"
            href="/buy-a-home"
            ctaLabel="Read More"
          />
          <ArticlePreviewCard
            title="Why Property Preparation Pays Off"
            excerpt="Well-prepared homes show better, photograph better, and often create stronger momentum once they reach the market."
            category="Property Preparation"
            meta="Article"
            image="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=900&q=80"
            imageAlt="Well-prepared residential interior with polished finishes"
            href="/resources"
            ctaLabel="Read Article"
          />
        </div>
      </div>
    </ContentSection>

    <ContentSection variant="dark">
      <PatternLabel
        id="CARD-IMAGE-01"
        name="ImageFeatureCard"
        description="Reusable premium image-led card for services, feature paths, investment opportunities, and homepage story blocks."
      />
      <div className="mx-auto max-w-[1220px] grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-stretch">
        <ImageFeatureCard
          eyebrow="Overlay Variant"
          title="Property Preparation Services"
          description="Designed for more visual service storytelling where the image leads and the message remains highly legible."
          image={servicePrepImg}
          imageAlt="Well-staged living room showing polished property presentation"
          href="/property-preparation"
          ctaLabel="View Services"
          overlay
        />
        <div className="grid grid-cols-1 gap-6 auto-rows-fr">
          <ImageFeatureCard
            eyebrow="Standard Variant"
            title="Work With Us"
            description="A warm image-above-text card for homepage features, service paths, and premium content rows."
            image={serviceBuyImg}
            imageAlt="Elegant residential exterior with strong curb appeal"
            href="/work-with-us"
            ctaLabel="Learn More"
            variant="light"
          />
          <ImageFeatureCard
            eyebrow="Dark Surface"
            title="Investment Opportunities"
            description="Use the darker variant when the card needs more contrast or a richer editorial tone."
            image={serviceInvestImg}
            imageAlt="Modern condominium building suited for investment marketing"
            href="/invest"
            ctaLabel="Explore Investing"
            variant="dark"
          />
        </div>
      </div>
    </ContentSection>

    <ContentSection className="pt-12 md:pt-16">
      <PatternLabel
        id="CARD-EDITORIAL-01"
        name="EditorialFeatureCard"
        description="Text-led premium card for trust points, service highlights, value propositions, and explanatory sections. Supports light, accent, and minimal variants."
      />
      <div className="mx-auto max-w-[1220px] grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 md:gap-10 items-start">
        <EditorialFeatureCard
          eyebrow="Service Highlight"
          index="01"
          title="Strategic Guidance From the Beginning"
          description="Use the light variant when the section needs structure and polish without feeling heavy. It works well beside imagery or inside premium service sections."
          icon={Compass}
          href="/buy-a-home"
          ctaLabel="Explore Buying"
          variant="light"
        />
        <EditorialFeatureCard
          eyebrow="Trust Point"
          index="02"
          title="A More Thoughtful Seller Experience"
          description="The accent variant creates a richer editorial surface for trust messaging, credentials, and narrative content that benefits from a warmer brand presence."
          icon={Home}
          href="/sell-your-home"
          ctaLabel="View Seller Strategy"
          variant="accent"
        />
      </div>
      <div className="mx-auto max-w-[1220px] mt-10 md:mt-12">
        <EditorialFeatureCard
          eyebrow="Minimal Variant"
          index="03"
          title="Decisions Rooted in Long-Term Value"
          description="The minimal variant is intended for quieter editorial moments where rhythm, spacing, and typography do most of the work. It should feel closer to a designed content block than a traditional card."
          variant="minimal"
        />
      </div>
    </ContentSection>

    {/* 4a. MediaHero */}
    <ContentSection>
      <PatternLabel id="HERO-MEDIA-01" name="MediaHero" description="Full-bleed hero with background image/video, headline, CTA, and optional glass overlay cards." />
    </ContentSection>
    <MediaHero
      media="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80"
      headline="Extraordinary Homes for Extraordinary Lives"
      subtext="Discover curated luxury properties across Massachusetts and the Dominican Republic."
      cta={{ text: "Explore Properties", href: "/listings" }}
      overlayCards={[
        { title: "Buyer Advisory", description: "White-glove guidance from search to close.", cta: { text: "Learn More", href: "/buy-a-home" } },
        { title: "Investment Strategy", description: "Data-driven portfolio growth in emerging markets.", cta: { text: "Invest Now", href: "/invest-in-real-estate" } },
        { title: "Interior Design", description: "Bespoke staging and renovation consulting.", cta: { text: "Get Started", href: "/interior-design" } },
      ]}
    />

    {/* 4b. FeaturedPropertyCard */}
    <ContentSection>
      <PatternLabel id="CARD-PROPERTY-01" name="FeaturedPropertyCard" description="Luxury listing card with hover effects, status badge, and optional logo watermark." />
      <div className="max-w-md">
        <FeaturedPropertyCard
          image="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80"
          status="For Sale"
          price="$2,450,000"
          address="142 Beacon Hill Drive, Boston, MA 02108"
          beds={5}
          baths={4}
          sqft="4,200"
        />
      </div>
    </ContentSection>

    {/* 4c. PropertyGrid */}
    <ContentSection variant="dark">
      <PatternLabel id="GRID-PROPERTY-01" name="PropertyGrid — 3 Columns" description="Responsive grid wrapper for FeaturedPropertyCard components." />
      <PropertyGrid
        columns={3}
        listings={[
          { image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80", status: "For Sale", price: "$1,850,000", address: "78 Commonwealth Ave, Boston, MA", beds: 4, baths: 3, sqft: "3,600" },
          { image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80", status: "Under Contract", price: "$3,200,000", address: "15 Oceanfront Blvd, Punta Cana, DR", beds: 6, baths: 5, sqft: "5,800" },
          { image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80", status: "New Listing", price: "$975,000", address: "221 Elm Street, Cambridge, MA", beds: 3, baths: 2, sqft: "2,100" },
        ]}
      />
    </ContentSection>

    {/* 4d. HorizontalScrollSection */}
    <ContentSection>
      <PatternLabel id="SCROLL-HORIZONTAL-01" name="HorizontalScrollSection" description="Horizontally scrollable showcase with snap behavior and text overlays. Ideal for developments." />
    </ContentSection>
    <section className="py-4">
      <div className="mx-auto max-w-wide px-5 md:px-12">
        <HorizontalScrollSection
          items={[
            { image: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80", title: "The Residences at Beacon", location: "Boston, MA" },
            { image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80", title: "Oceanview Villas", location: "Punta Cana, DR" },
            { image: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80", title: "Cambridge Modern", location: "Cambridge, MA" },
            { image: "https://images.unsplash.com/photo-1600607687644-aac4c3eac7f4?w=800&q=80", title: "Las Terrenas Collection", location: "Samaná, DR" },
            { image: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800&q=80", title: "Back Bay Brownstones", location: "Boston, MA" },
          ]}
        />
      </div>
    </section>

    {/* 4e. StatsSplit */}
    <ContentSection variant="dark">
      <PatternLabel id="LAYOUT-STATS-01" name="StatsSplit" description="Text + metrics + visual in a split layout. Combines narrative with quantitative proof." />
      <StatsSplit
        headline="Proven Results, Trusted Relationships"
        description="Our team has guided hundreds of families and investors through successful transactions across two countries, consistently exceeding expectations."
        stats={[
          { value: "$120M+", label: "Transaction Volume" },
          { value: "350+", label: "Families Served" },
          { value: "98%", label: "Client Satisfaction" },
          { value: "15+", label: "Years Experience" },
        ]}
        image="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80"
        imageAlt="Luxury waterfront property"
        cta={{ text: "Our Story", href: "/about" }}
      />
    </ContentSection>

    {/* 4f. OverlayContentCard */}
    <ContentSection>
      <PatternLabel id="CARD-OVERLAY-01" name="OverlayContentCard" description="Composable info boxes in glass and solid variants. Designed for use inside heroes or over images." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative overflow-hidden aspect-[4/3]">
          <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-rich-black/40" />
          <div className="absolute bottom-4 left-4 right-4">
            <OverlayContentCard variant="glass" title="Glass Variant" description="Frosted glass with backdrop blur — ideal for overlaying on images and video heroes." cta={{ text: "Learn More", href: "/about" }} />
          </div>
        </div>
        <div className="flex items-end">
          <OverlayContentCard variant="solid" title="Solid Variant" description="Standard card style using background and border tokens. Works in any layout context." cta={{ text: "Explore", href: "/listings" }} />
        </div>
      </div>
    </ContentSection>

    {/* 4g. BuyerStrategySection */}
    <ContentSection>
      <PatternLabel
        id="SECTION-BUYER-STRATEGY-01"
        name="Buyer Strategy Section"
        description="A premium split-layout section used to communicate strategic guidance with supporting imagery and feature highlights."
      />
    </ContentSection>
    <BuyerStrategySection
      subheading="Buyer Strategy"
      heading="Buying a home should feel clear, strategic, and aligned with your future."
      description="A premium split-layout section designed for strategic service messaging, with strong imagery, clear supporting copy, and a reusable feature grid that can flex across buyer, seller, investor, or design pages."
      image={ctaBuyImg}
      imageAlt="Luxury home exterior for buyer strategy section example"
      cta={{ label: "Book a Consultation", href: "/consultation" }}
      secondaryCta={{ label: "Explore Listings", href: "/listings" }}
      features={[
        {
          icon: Search,
          title: "Clear next steps",
          description: "Guide users through what happens first, what matters now, and what comes next.",
        },
        {
          icon: BarChart3,
          title: "Market-aware advice",
          description: "Support stronger decision-making with context, strategy, and timing insight.",
        },
        {
          icon: Users,
          title: "Trusted relationships",
          description: "Highlight the value of curated partners, vendors, or lender networks.",
        },
        {
          icon: TrendingUp,
          title: "Long-term value",
          description: "Frame the offer around enduring fit, future upside, and smarter choices.",
        },
      ]}
      className="pt-0"
    />
    <ContentSection>
      <div className="border border-border bg-card p-6 md:p-8 space-y-5">
        <div>
          <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-2">Component ID</p>
          <p className="font-body text-base text-foreground">{buyerStrategySectionMeta.componentId}</p>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-2">Description</p>
          <p className="text-muted-foreground text-base leading-relaxed">
            A premium split-layout section used to communicate strategic guidance with supporting imagery and feature highlights.
          </p>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-2">Props Structure Example</p>
          <pre className="overflow-x-auto bg-secondary text-secondary-foreground p-5 text-sm leading-relaxed">
{`{
  heading: string
  description: string
  image: string
  cta: {
    label: string
    href: string
  }
  features: {
    title: string
    description: string
  }[]
}`}
          </pre>
        </div>
      </div>
    </ContentSection>

    {/* ════════════════════════════════════════════════════════════
        SECTION 5 — PAGE HERO VARIANTS
       ════════════════════════════════════════════════════════════ */}
    <ContentSection>
      <SectionLabel>5 · Page Hero Variants</SectionLabel>
      <p className="text-muted-foreground text-sm leading-relaxed">Reusable hero patterns for different page types. Each variant serves a distinct purpose and layout need.</p>
    </ContentSection>

    {/* 5a. DefaultHero — centered with image */}
    <ContentSection>
      <PatternLabel id="HERO-DEFAULT-01a" name="DefaultHero — Centered with Image" description="Full hero with background image, headline, subtext, and dual CTAs. Used on About, Buy, Sell, Invest pages." />
    </ContentSection>
    <DefaultHero
      headline="Your Trusted Partner in Luxury Real Estate"
      subtext="From first-time homebuyers in Massachusetts to seasoned investors in the Dominican Republic — we deliver white-glove service at every price point."
      primaryCta={{ text: "Book a Consultation", href: "/consultation" }}
      secondaryCta={{ text: "View Listings", href: "/listings" }}
      backgroundImage="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80"
    />

    {/* 5b. DefaultHero — left-aligned without image */}
    <ContentSection>
      <PatternLabel id="HERO-DEFAULT-01b" name="DefaultHero — Left-Aligned, No Image" description="Clean hero on secondary background with left-aligned text. Used for service pages where imagery isn't needed." />
    </ContentSection>
    <DefaultHero
      headline="Sell Your Home with Confidence"
      subtext="Strategic pricing, professional staging, and targeted marketing ensure your property reaches the right buyers at the right time."
      primaryCta={{ text: "Get a Free Valuation", href: "/consultation" }}
      align="left"
    />

    {/* 5c. SearchHero */}
    <ContentSection>
      <PatternLabel id="HERO-SEARCH-01" name="SearchHero" description="Premium hero with integrated property search module. Includes location, category, and type dropdowns with quick-filter tags. Used on Homepage, Listings, and Dominican Republic pages." />
    </ContentSection>
    <SearchHero
      headline="Find Your Extraordinary Home"
      subtext="Search luxury properties across Massachusetts and the Dominican Republic."
      backgroundImage="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1920&q=80"
    />

    {/* 5c-ii. SearchFilterThin */}
    <ContentSection>
      <PatternLabel id="SEARCH-FILTER-THIN-01" name="SearchFilterThin" description="Compact, single-row filter bar with glass effect. Designed for Listings, Buy, and interior pages where a full hero search is not needed. Stacks vertically on mobile." />
      <SearchFilterThin />
    </ContentSection>

    <ContentSection>
      <PatternLabel id="HERO-MINIMAL-01a" name="MinimalHero — Light" description="Compact, text-only hero with optional breadcrumbs. Used for Blog, Buyer Guides, FAQ, and legal pages." />
    </ContentSection>
    <MinimalHero
      title="Frequently Asked Questions"
      breadcrumb={[
        { label: "Home", href: "/" },
        { label: "Resources", href: "/resources" },
        { label: "FAQ" },
      ]}
    />

    {/* 5e. MinimalHero — dark */}
    <ContentSection>
      <PatternLabel id="HERO-MINIMAL-01b" name="MinimalHero — Dark" description="Dark variant for pages that transition into dark content sections." />
    </ContentSection>
    <MinimalHero
      title="Privacy Policy"
      breadcrumb={[
        { label: "Home", href: "/" },
        { label: "Privacy Policy" },
      ]}
      variant="dark"
    />

    {/* ════════════════════════════════════════════════════════════
        SECTION 6 — ADVANCED UX PATTERNS
       ════════════════════════════════════════════════════════════ */}
    <ContentSection>
      <SectionLabel>6 · Advanced UX Patterns</SectionLabel>
      <p className="text-muted-foreground text-sm leading-relaxed">High-impact elements that improve UX, structure, and conversion across the site.</p>
    </ContentSection>

    {/* 5a. StickyNav */}
    <ContentSection>
      <PatternLabel id="UX-STICKY-NAV-01" name="StickyNav" description="Horizontal anchor-based navigation that sticks on scroll. Used on long pages like Buy, Sell, Invest." />
      <div className="border border-border overflow-hidden">
        <StickyNav
          items={[
            { label: "Overview", href: "#overview" },
            { label: "Services", href: "#services" },
            { label: "Process", href: "#process" },
            { label: "Testimonials", href: "#testimonials" },
            { label: "Contact", href: "#contact" },
          ]}
        />
      </div>
    </ContentSection>

    {/* 5b. StatusBadge */}
    <ContentSection variant="dark">
      <PatternLabel id="UI-BADGE-01" name="StatusBadge" description="Contextual status badges for property cards and listings. Six predefined variants." />
      <div className="flex flex-wrap gap-3">
        <StatusBadge variant="for-sale" />
        <StatusBadge variant="sold" />
        <StatusBadge variant="featured" />
        <StatusBadge variant="new" />
        <StatusBadge variant="investment" />
        <StatusBadge variant="under-contract" />
      </div>
    </ContentSection>

    {/* 5c. ComparisonBlock */}
    <ContentSection>
      <PatternLabel id="UX-COMPARISON-01" name="ComparisonBlock" description="Structured comparison table for decision-making. Supports check/x/dash/text values and column highlighting." />
      <ComparisonBlock
        rows={[
          "Build equity over time",
          "Tax deductions available",
          "Full customization freedom",
          "Fixed monthly payment",
          "No maintenance responsibility",
          "Flexibility to relocate",
        ]}
        columns={[
          {
            title: "Buying",
            highlighted: true,
            values: [true, true, true, true, false, false],
          },
          {
            title: "Renting",
            values: [false, false, false, false, true, true],
          },
        ]}
      />
    </ContentSection>

    {/* 5d. Timeline */}
    <ContentSection variant="dark">
      <PatternLabel id="UX-TIMELINE-01" name="Timeline" description="Visual step-based journey timeline. More visual than ProcessSteps — ideal for buyer/seller journeys." />
      <Timeline
        variant="dark"
        steps={[
          { year: "Week 1–2", title: "Discovery & Pre-Approval", description: "Initial consultation to understand your goals, followed by lender pre-approval coordination." },
          { year: "Week 3–6", title: "Property Search & Tours", description: "Curated shortlist of properties matching your criteria with guided private tours." },
          { year: "Week 6–7", title: "Offer & Negotiation", description: "Strategic offer preparation and skilled negotiation to secure the best terms." },
          { year: "Week 7–10", title: "Due Diligence", description: "Inspection, appraisal, and legal review coordinated by our team." },
          { year: "Week 10–12", title: "Closing Day", description: "Final walkthrough, document signing, and key handover — welcome home." },
        ]}
      />
    </ContentSection>

    {/* 5e. LeadCapture */}
    <ContentSection>
      <PatternLabel id="UX-LEAD-01" name="LeadCapture" description="Inline CTA block for guides, downloads, and lead generation. Supports email input and multiple visual variants." />
      <div className="space-y-6">
        <LeadCapture
          title="Book a Free Consultation"
          description="Connect with our team and get clear on your next steps in the Massachusetts or Dominican Republic market."
          ctaText="Download Now"
          variant="accent"
        />
        <LeadCapture
          title="Get Market Updates"
          description="Weekly insights on listings, pricing trends, and investment opportunities."
          ctaText="Subscribe"
          showEmailInput
          variant="light"
        />
      </div>
    </ContentSection>

    {/* 5f. PropertyDetailLayout */}
    <ContentSection variant="dark">
      <PatternLabel id="LAYOUT-PROPERTY-01" name="PropertyDetailLayout" description="Full property detail view with image gallery, specs, description, and CTA. Used for individual listing pages." />
      <PropertyDetailLayout
        variant="dark"
        images={[
          "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80",
          "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80",
          "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80",
          "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80",
        ]}
        price="$2,450,000"
        address="142 Beacon Hill Drive, Boston, MA 02108"
        status="for-sale"
        beds={5}
        baths={4}
        sqft="4,200"
        yearBuilt="2019"
        description="This stunning contemporary residence offers panoramic city views from every level. Featuring an open-concept main floor with chef's kitchen, imported Italian marble, and floor-to-ceiling windows. The primary suite includes a spa-inspired bath with heated floors and a private balcony. Smart home technology throughout, three-car garage, and meticulously landscaped grounds."
        cta={{ text: "Schedule a Viewing", href: "/consultation" }}
      />
    </ContentSection>

    {/* 5g. InfoCard */}
    <ContentSection>
      <PatternLabel id="UI-INFOCARD-01" name="InfoCard" description="Compact highlight cards for stats, tips, and insights. Three variants: default, highlight, outline." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <InfoCard
          icon={DollarSign}
          title="Average Home Price"
          value="$625K"
          description="Median home price in Greater Boston as of Q4 2025."
          variant="default"
        />
        <InfoCard
          icon={TrendingUp}
          title="Annual Appreciation"
          value="8.2%"
          description="Year-over-year property value increase in target markets."
          variant="highlight"
        />
        <InfoCard
          icon={Key}
          title="Days on Market"
          value="18"
          description="Average days to sale for Hadassah Homes listings."
          variant="outline"
        />
      </div>
    </ContentSection>

    {/* 5h. MultiCTA */}
    <ContentSection>
      <PatternLabel id="CTA-MULTI-01" name="MultiCTA" description="Section with 2–3 CTA paths for guiding different user intents. Supports light and dark variants." />
    </ContentSection>
    <MultiCTA
      eyebrow="How Can We Help?"
      headline="Choose Your Path"
      paths={[
        { icon: Home, title: "Buy a Home", description: "Find your dream home with expert guidance through every step of the buying process.", image: ctaBuyImg, cta: { text: "Start Buying", href: "/buy-a-home" } },
        { icon: BarChart3, title: "Sell Your Property", description: "Maximize your sale price with strategic marketing and expert negotiation.", image: ctaSellImg, cta: { text: "Start Selling", href: "/sell-your-home" } },
        { icon: TrendingUp, title: "Invest in Real Estate", description: "Build wealth through carefully selected investment opportunities.", image: ctaInvestImg, cta: { text: "Start Investing", href: "/invest-in-real-estate" } },
      ]}
    />

    {/* 5i. IconSystem */}
    <ContentSection>
      <PatternLabel id="UI-ICON-01" name="Icon System" description="Standardized icon usage with consistent sizing, spacing, and variant styles across all components." />
      <IconShowcase
        icons={[
          { icon: Home, label: "Home" },
          { icon: Building, label: "Building" },
          { icon: Key, label: "Key" },
          { icon: Search, label: "Search" },
          { icon: Heart, label: "Heart" },
          { icon: Star, label: "Star" },
          { icon: DollarSign, label: "Dollar" },
          { icon: TrendingUp, label: "Trending" },
          { icon: MapPin, label: "Location" },
          { icon: Phone, label: "Phone" },
          { icon: Calendar, label: "Calendar" },
          { icon: Users, label: "Users" },
          { icon: Shield, label: "Shield" },
          { icon: Globe, label: "Globe" },
          { icon: BarChart3, label: "Chart" },
          { icon: Paintbrush, label: "Design" },
        ]}
      />
    </ContentSection>

    {/* ════════════════════════════════════════════════════════════
        SECTION — ACCESSIBILITY STANDARDS
       ════════════════════════════════════════════════════════════ */}
    <ContentSection>
      <SectionLabel>Accessibility Standards (WCAG 2.1 AA)</SectionLabel>

      {/* Focus States */}
      <PatternLabel id="A11Y-FOCUS-01" name="Focus States" description="All interactive elements have visible focus rings using the design system's ring color. Tab through elements to verify." />
      <div className="space-y-6 mb-12">
        <div className="flex flex-wrap gap-4">
          <button className="bg-accent text-accent-foreground font-body text-sm uppercase tracking-widest px-8 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            Focused Button
          </button>
          <button className="border-2 border-primary bg-transparent text-primary font-body text-sm uppercase tracking-widest px-8 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            Outline Focus
          </button>
          <a href="#" className="font-body text-sm uppercase tracking-widest text-accent underline-offset-4 hover:underline px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            Link Focus
          </a>
        </div>
        <div className="max-w-sm">
          <label htmlFor="a11y-demo-input" className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5 block">Demo Input</label>
          <input
            id="a11y-demo-input"
            type="text"
            placeholder="Tab here to see focus ring"
            className="w-full bg-background border-2 border-border px-4 py-3 font-body text-sm text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-all"
          />
        </div>
      </div>

      {/* Contrast */}
      <PatternLabel id="A11Y-CONTRAST-01" name="Contrast-Safe Text" description="Text on all backgrounds meets 4.5:1 for body and 3:1 for large text. Dark overlays ensure text readability on images." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
        <div className="bg-secondary p-6">
          <p className="text-secondary-foreground text-sm mb-1">Body on Deep Green — ✅ 4.5:1+</p>
          <h3 className="font-headline text-2xl text-secondary-foreground">Large Text — ✅ 3:1+</h3>
        </div>
        <div className="bg-primary p-6">
          <p className="text-primary-foreground text-sm mb-1">Body on Rich Black — ✅ 4.5:1+</p>
          <h3 className="font-headline text-2xl text-accent">Gold on Black — ✅ 3:1+</h3>
        </div>
        <div className="bg-background border border-border p-6">
          <p className="text-foreground text-sm mb-1">Body on Cream — ✅ 4.5:1+</p>
          <p className="text-muted-foreground text-sm">Muted on Cream — ✅ 4.5:1+</p>
        </div>
        <div className="relative p-6 overflow-hidden min-h-[120px] flex items-end">
          <div className="absolute inset-0 bg-muted" />
          <div className="absolute inset-0 bg-gradient-to-t from-rich-black/80 to-rich-black/40" />
          <div className="relative z-10">
            <p className="text-cream text-sm mb-1">Text on image overlay — ✅ 4.5:1+</p>
            <h3 className="font-headline text-2xl text-cream">Overlay Heading</h3>
          </div>
        </div>
      </div>

      {/* Semantic HTML */}
      <PatternLabel id="A11Y-SEMANTIC-01" name="Semantic Structure" description="Proper HTML5 landmarks and ARIA labels used throughout. Heading hierarchy follows h1 → h2 → h3 order." />
      <div className="bg-card border border-border p-6 mb-12 font-mono text-sm text-muted-foreground space-y-1.5">
        <p>&lt;a class="sr-skip-link"&gt; — Skip to main content</p>
        <p>&lt;nav aria-label="Main navigation"&gt;</p>
        <p>&lt;main id="main-content"&gt;</p>
        <p>&lt;section aria-label="..."&gt;</p>
        <p>&lt;nav aria-label="Page sections"&gt; — StickyNav</p>
        <p>&lt;footer role="contentinfo"&gt;</p>
        <p>&lt;form aria-label="..."&gt; — LeadCapture, SearchHero</p>
        <p>&lt;label htmlFor="..."&gt; — All form inputs</p>
      </div>

      {/* Motion */}
      <PatternLabel id="A11Y-MOTION-01" name="Reduced Motion" description="All animations and transitions respect prefers-reduced-motion. Users who prefer reduced motion see no animations." />
      <div className="bg-card border border-border p-6 mb-12 font-mono text-sm text-muted-foreground space-y-1.5">
        <p>@media (prefers-reduced-motion: reduce) {"{"}</p>
        <p className="pl-4">animation-duration: 0.01ms !important;</p>
        <p className="pl-4">transition-duration: 0.01ms !important;</p>
        <p className="pl-4">scroll-behavior: auto !important;</p>
        <p>{"}"}</p>
      </div>

      {/* ARIA summary */}
      <PatternLabel id="A11Y-ARIA-01" name="ARIA Attributes" description="Interactive components use proper ARIA attributes for screen reader support." />
      <div className="overflow-x-auto border border-border bg-card mb-8">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-border">
              <th className="text-left p-4 font-body text-xs uppercase tracking-widest text-muted-foreground">Component</th>
              <th className="text-left p-4 font-body text-xs uppercase tracking-widest text-muted-foreground">ARIA Used</th>
            </tr>
          </thead>
          <tbody className="text-muted-foreground">
            <tr className="border-b border-border"><td className="p-4 font-medium text-foreground">SearchHero</td><td className="p-4">aria-label, htmlFor, aria-hidden on icons</td></tr>
            <tr className="border-b border-border"><td className="p-4 font-medium text-foreground">Navbar</td><td className="p-4">aria-label, aria-expanded, aria-label on toggles</td></tr>
            <tr className="border-b border-border"><td className="p-4 font-medium text-foreground">AccordionGroup</td><td className="p-4">aria-expanded, aria-controls (Radix built-in)</td></tr>
            <tr className="border-b border-border"><td className="p-4 font-medium text-foreground">ComparisonBlock</td><td className="p-4">role="img", aria-label on icons</td></tr>
            <tr className="border-b border-border"><td className="p-4 font-medium text-foreground">LeadCapture</td><td className="p-4">aria-label on form, sr-only label, aria-required</td></tr>
            <tr className="border-b border-border"><td className="p-4 font-medium text-foreground">StickyNav</td><td className="p-4">aria-label="Page sections"</td></tr>
            <tr><td className="p-4 font-medium text-foreground">MediaHero</td><td className="p-4">aria-label on section, aria-hidden on decorative media</td></tr>
          </tbody>
        </table>
      </div>
    </ContentSection>

    {/* ════════════════════════════════════════════════════════════
        SECTION 7 — INTERACTIVE PATTERNS & MOTION
       ════════════════════════════════════════════════════════════ */}
    <ContentSection>
      <SectionLabel>7 · Interactive Patterns & Motion</SectionLabel>
      <p className="text-muted-foreground text-sm leading-relaxed">Premium interactive components and motion patterns. All respect prefers-reduced-motion and maintain keyboard accessibility.</p>
    </ContentSection>

    {/* 7a. Motion Tokens */}
    <ContentSection>
      <PatternLabel id="MOTION-SYSTEM-01" name="Motion Standards" description="Standardized duration, easing, and entrance tokens. Use CSS custom properties for consistency across all interactive components." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-card border border-border p-6">
          <h4 className="font-headline text-lg text-foreground mb-4">Duration Tokens</h4>
          <div className="space-y-3 font-mono text-sm text-muted-foreground">
            <p><span className="text-accent">--duration-fast</span>: 200ms — hover, micro-interactions</p>
            <p><span className="text-accent">--duration-standard</span>: 400ms — tab switches, reveals</p>
            <p><span className="text-accent">--duration-slow</span>: 700ms — entrance animations, stagger</p>
          </div>
        </div>
        <div className="bg-card border border-border p-6">
          <h4 className="font-headline text-lg text-foreground mb-4">Easing Tokens</h4>
          <div className="space-y-3 font-mono text-sm text-muted-foreground">
            <p><span className="text-accent">--easing-standard</span>: cubic-bezier(0.22, 1, 0.36, 1)</p>
            <p className="text-xs">Smooth deceleration — used for most transitions</p>
            <p className="mt-3"><span className="text-accent">--easing-premium</span>: cubic-bezier(0.16, 1, 0.3, 1)</p>
            <p className="text-xs">Stronger overshoot — used for emphasis moments</p>
          </div>
        </div>
      </div>
      <div className="bg-card border border-border p-6">
        <h4 className="font-headline text-lg text-foreground mb-4">Pattern Reference</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 font-mono text-xs text-muted-foreground">
          <div><span className="text-accent block mb-1">fade-up</span>opacity 0→1, translateY 24px→0</div>
          <div><span className="text-accent block mb-1">hover-lift</span>translateY -6px, shadow increase</div>
          <div><span className="text-accent block mb-1">scale-subtle</span>scale 1→1.03 on images</div>
          <div><span className="text-accent block mb-1">stagger</span>60-80ms delay per child</div>
          <div><span className="text-accent block mb-1">slide-indicator</span>left/width transition on tabs</div>
          <div><span className="text-accent block mb-1">reduced-motion</span>all animations disabled</div>
        </div>
      </div>
    </ContentSection>

    {/* 7b. RevealSection */}
    <ContentSection variant="dark">
      <PatternLabel id="MOTION-REVEAL-01" name="RevealSection" description="Scroll-triggered fade+translate wrapper. Fires once on intersection. Supports direction (up/down/left/right) and stagger delay." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <RevealSection delay={0}>
          <div className="bg-card border border-border p-6">
            <h4 className="font-headline text-lg text-card-foreground mb-2">First Block</h4>
            <p className="text-muted-foreground text-sm">Reveals immediately on scroll intersection.</p>
          </div>
        </RevealSection>
        <RevealSection delay={120}>
          <div className="bg-card border border-border p-6">
            <h4 className="font-headline text-lg text-card-foreground mb-2">Second Block</h4>
            <p className="text-muted-foreground text-sm">Staggered 120ms after the first.</p>
          </div>
        </RevealSection>
        <RevealSection delay={240}>
          <div className="bg-card border border-border p-6">
            <h4 className="font-headline text-lg text-card-foreground mb-2">Third Block</h4>
            <p className="text-muted-foreground text-sm">Staggered 240ms after the first.</p>
          </div>
        </RevealSection>
      </div>
    </ContentSection>

    {/* 7c. HoverPremiumCard */}
    <ContentSection>
      <PatternLabel id="INTERACT-HOVER-CARD-01" name="HoverPremiumCard" description="Subtle lift, shadow, and border emphasis on hover. Image scale on hover. No exaggerated movement — premium restraint." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto max-w-wide">
        <HoverPremiumCard
          icon={Home}
          title="Buyer Advisory"
          description="White-glove guidance from initial consultation through closing day. We handle every detail."
          image="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80"
        />
        <HoverPremiumCard
          icon={TrendingUp}
          title="Investment Strategy"
          description="Data-driven analysis and emerging market access to build a diversified real estate portfolio."
          image="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80"
        />
        <HoverPremiumCard
          icon={Paintbrush}
          title="Interior Design"
          description="Bespoke staging and renovation consulting that elevates your property's appeal and value."
          image="https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=600&q=80"
        />
      </div>
    </ContentSection>

    {/* 7d. StackingCards */}
    <ContentSection variant="dark">
      <PatternLabel id="INTERACT-STACK-CARDS-01" name="StackingCards" description="Scroll-driven stacking card section for editorial storytelling. Cards stick and stack with subtle depth scaling. Ideal for process steps, neighborhoods, or service breakdowns." />
    </ContentSection>
    <section className="py-8 md:py-16 bg-secondary">
      <div className="mx-auto max-w-wide px-5 md:px-12">
        <StackingCards
          cards={[
            {
              eyebrow: "Step 01",
              title: "Discovery & Strategy",
              description: "We begin with a comprehensive consultation to understand your goals, timeline, preferences, and budget. Our team develops a personalized strategy tailored to your unique situation.",
              image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80",
            },
            {
              eyebrow: "Step 02",
              title: "Property Search & Curation",
              description: "Using deep market knowledge and proprietary data, we curate a shortlist of properties that match your criteria — saving you time and ensuring you see only the best options.",
              image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80",
            },
            {
              eyebrow: "Step 03",
              title: "Negotiation & Closing",
              description: "Our experienced negotiators secure the best terms on your behalf. We coordinate inspections, appraisals, legal review, and closing logistics for a seamless transaction.",
              image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80",
            },
          ]}
        />
      </div>
    </section>

    {/* 7e. StickyStory */}
    <ContentSection>
      <PatternLabel id="INTERACT-STICKY-STORY-01" name="StickyStory" description="Sticky media column with scrolling narrative blocks. Premium editorial pattern for investment journeys, process walkthroughs, or brand storytelling." />
    </ContentSection>
    <section className="py-8 md:py-16">
      <div className="mx-auto max-w-wide px-5 md:px-12">
        <StickyStory
          image="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80"
          imageAlt="Luxury waterfront property"
          steps={[
            {
              eyebrow: "The Opportunity",
              title: "Caribbean Real Estate Is Booming",
              description: "The Dominican Republic has emerged as one of the fastest-growing real estate markets in the Caribbean, with foreign investment increasing 35% year-over-year. Strategic positioning now means long-term wealth building.",
            },
            {
              eyebrow: "Our Advantage",
              title: "Local Expertise, Global Standards",
              description: "Unlike remote advisory firms, our team operates on the ground in key Dominican Republic markets. We navigate local regulations, vet developers, and coordinate every detail of your acquisition.",
            },
            {
              eyebrow: "The Result",
              title: "Properties That Perform",
              description: "Our curated portfolio consistently outperforms market averages. From beachfront villas to urban developments, every opportunity is vetted for appreciation potential and rental yield.",
            },
          ]}
        />
      </div>
    </section>

    {/* 7f. ImageCallout */}
    <ContentSection variant="dark">
      <PatternLabel id="INTERACT-IMAGE-CALLOUT-01" name="ImageCallout" description="Image with positioned hotspot callouts. Hover to reveal details on desktop. Stacked cards on mobile for full accessibility." />
    </ContentSection>
    <section className="py-8 md:py-16 bg-secondary">
      <div className="mx-auto max-w-wide px-5 md:px-12">
        <ImageCallout
          image="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80"
          imageAlt="Luxury property exterior with labeled features"
          callouts={[
            { title: "Smart Home System", description: "Integrated Lutron lighting and Sonos audio throughout all living spaces.", x: 15, y: 30 },
            { title: "Imported Stone", description: "Italian marble countertops and travertine flooring in all bathrooms.", x: 50, y: 50 },
            { title: "Outdoor Living", description: "Professional landscape design with heated pool and covered entertaining area.", x: 75, y: 65 },
            { title: "Energy Efficient", description: "Solar-ready roof, triple-pane windows, and high-efficiency HVAC system.", x: 30, y: 15 },
          ]}
        />
      </div>
    </section>

    {/* 7g. AnimatedTabs */}
    <ContentSection>
      <PatternLabel id="INTERACT-TABS-ANIMATED-01" name="AnimatedTabs" description="Tab content with sliding indicator and smooth crossfade transitions. Full keyboard support with arrow key navigation." />
      <AnimatedTabs
        tabs={[
          {
            label: "Massachusetts",
            content: (
              <div className="space-y-3">
                <h3 className="font-headline text-xl text-foreground">Massachusetts Market</h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">Greater Boston continues to be one of the most resilient real estate markets in the country. Strong employment, world-class institutions, and limited housing supply create consistent demand and appreciation across the North Shore, Cambridge, and Cape Cod.</p>
              </div>
            ),
          },
          {
            label: "Dominican Republic",
            content: (
              <div className="space-y-3">
                <h3 className="font-headline text-xl text-foreground">Dominican Republic Market</h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">The DR offers compelling investment fundamentals: favorable tax incentives, growing tourism infrastructure, and a 35% year-over-year increase in foreign property investment. Key markets include Punta Cana, Santo Domingo, and Las Terrenas.</p>
              </div>
            ),
          },
          {
            label: "Investment Analysis",
            content: (
              <div className="space-y-3">
                <h3 className="font-headline text-xl text-foreground">Comparative Analysis</h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">Our dual-market expertise allows us to provide clients with comparative analysis across both markets. Whether you're optimizing for rental yield, capital appreciation, or lifestyle value, we help you allocate capital strategically.</p>
              </div>
            ),
          },
        ]}
      />
    </ContentSection>

    {/* ════════════════════════════════════════════════════════════
        REFERENCE SUMMARY
       ════════════════════════════════════════════════════════════ */}
    <ContentSection variant="dark">
      <SectionLabel>Reference Summary</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-secondary-foreground/70 leading-relaxed">
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">Fonts</h3>
          <p><strong className="text-secondary-foreground">Headline:</strong> EB Garamond (serif)</p>
          <p><strong className="text-secondary-foreground">Body:</strong> Manrope (sans-serif)</p>
        </div>
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">Palette</h3>
          <p><strong className="text-secondary-foreground">Cream:</strong> hsl(40, 23%, 96%)</p>
          <p><strong className="text-secondary-foreground">Gold:</strong> hsl(43, 43%, 56%)</p>
          <p><strong className="text-secondary-foreground">Deep Green:</strong> hsl(150, 32%, 18%)</p>
          <p><strong className="text-secondary-foreground">Rich Black:</strong> hsl(0, 0%, 4%)</p>
        </div>
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">Navigation (1)</h3>
          <p><strong className="text-secondary-foreground">NAV-PRIMARY-01:</strong> Premium sticky navbar with transparent→solid transition, centered links, dropdown menus with hover delay, and prominent "Book a Consultation" CTA. Responsive mobile drawer with accordion groups.</p>
        </div>
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">Core Components (10)</h3>
          <p>PageHero · ContentSection · SectionHeader · CardGrid · FeatureGrid · CTABlock · ProcessSteps · SplitSection · Breadcrumbs · Checklist</p>
        </div>
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">UI Elements (5)</h3>
          <p>AccordionGroup · TabSection · TrustBar · StatsSection · TestimonialCard</p>
        </div>
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">Premium Patterns (6)</h3>
          <p>MediaHero · FeaturedPropertyCard · PropertyGrid · HorizontalScrollSection · StatsSplit · OverlayContentCard</p>
        </div>
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">Page Hero Variants (3)</h3>
          <p>DefaultHero · SearchHero · MinimalHero</p>
        </div>
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">Advanced UX (9)</h3>
          <p>StickyNav · StatusBadge · ComparisonBlock · Timeline · LeadCapture · PropertyDetailLayout · InfoCard · MultiCTA · IconSystem</p>
        </div>
        <div className="space-y-2">
          <h3 className="font-headline text-lg text-secondary-foreground">Interactive Patterns (7)</h3>
          <p>RevealSection · HoverPremiumCard · StackingCards · StickyStory · ImageCallout · AnimatedTabs · Motion Tokens</p>
        </div>
      </div>
    </ContentSection>
  </SiteLayout>
);

export default StyleGuide;
